import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { t as tokens_module } from './tokens.module-03e95cfb.js';
import { g as getDialogSelector, s as setupDialogCompactHandler } from './dialog-1c8f6224.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';
import { c as createAndActivateFocusTrap } from './focus-trap-d9ee9cfa.js';
import { c as classNames } from './classnames-ba9ba5b9.js';
import { w as waitForAddedNodesToBeReady } from './mutation-observer-cf3cccee.js';

const marketBladeCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}@keyframes market-popup{from{opacity:0;transform:scale(0.9, 0.9)}to{opacity:1;transform:scale(1, 1)}}@keyframes market-popdown{from{opacity:1;transform:scale(1, 1)}to{opacity:0;transform:scale(0.9, 0.9)}}@keyframes market-slideup{from{opacity:0;transform:translateY(80vh)}to{opacity:1;transform:translateY(0)}}@keyframes market-slidedown{from{opacity:1;transform:translateY(0)}to{opacity:0;transform:translateY(80vh)}}@keyframes market-slide-left-enter{from{transform:translateX(100%)}to{transform:translateX(0)}}@keyframes market-slide-left-exit{from{transform:translateX(0)}to{transform:translateX(100%)}}@keyframes market-fade-in{from{opacity:0}to{opacity:1}}@keyframes market-fade-out{from{opacity:1}to{opacity:0}}@keyframes market-input-autofill-start{from{}to{}}@keyframes market-input-autofill-cancel{from{}to{}}@keyframes market-input-search-compact-enter{from{}to{}}@keyframes market-input-search-compact-exit{from{}to{}}:host{display:flex;flex-direction:column;justify-content:stretch;overflow:hidden}:host ::slotted(main){flex:0 1 100%;overflow-y:auto;height:100%}:host ::slotted(.market-header){margin-bottom:var(--core-metrics-spacing-300);padding-top:0}:host ::slotted(.market-footer){padding-bottom:0}:host{position:fixed;top:0;right:0;width:100%;max-width:calc(var(--modal-blade-width, 400px) + var(--core-metrics-spacing-400) + var(--core-metrics-spacing-400));height:100vh;padding-top:var(--core-metrics-spacing-400);padding-bottom:var(--core-metrics-spacing-400);background-color:var(--modal-full-background-color);box-shadow:0 2px 16px rgb(0 0 0 / 0.08), 0 4px 8px rgb(0 0 0 / 0.08);animation-name:market-slide-left-enter;animation-duration:var(\n      --blade-animation-enter-transition-duration,\n      var(--core-animation-enter-transition-moderate-speed-duration)\n    );animation-timing-function:var(\n      --blade-animation-enter-transition-easing,\n      var(--core-animation-enter-transition-easing)\n    );animation-fill-mode:forwards}:host ::slotted(*){padding-right:var(--core-metrics-spacing-400);padding-left:var(--core-metrics-spacing-400)}:host([hidden]){animation-name:market-slide-left-exit;animation-duration:var(\n        --blade-animation-exit-transition-duration,\n        var(--core-animation-exit-transition-moderate-speed-duration)\n      );animation-timing-function:var(\n        --blade-animation-exit-transition-easing,\n        var(--core-animation-exit-transition-easing)\n      )}:host([hidden].skip-animation){transform:translateX(100%);animation:none}";

const MarketBlade = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketDialogLoaded = createEvent(this, "marketDialogLoaded", 7);
    this.marketDialogDismissed = createEvent(this, "marketDialogDismissed", 7);
    this.marketDialogDidDismiss = createEvent(this, "marketDialogDidDismiss", 7);
    this.type = 'blade';
    this.observers = {};
    /* Used to skip the exit animation for <market-blade hidden> on load */
    this.skipAnimation = false;
    this.hidden = false;
    this.dialogID = undefined;
    this.index = undefined;
    this.animationDuration = tokens_module.CORE_ANIMATION_ENTER_TRANSITION_MODERATE_SPEED_DURATION;
    this.animationEnterDuration = tokens_module.CORE_ANIMATION_ENTER_TRANSITION_MODERATE_SPEED_DURATION;
    this.animationExitDuration = tokens_module.CORE_ANIMATION_EXIT_TRANSITION_MODERATE_SPEED_DURATION;
    this.trapFocus = false;
  }
  reenableAnimation() {
    this.skipAnimation = false;
  }
  /* Listen to the marketHeaderNavigate event emitted by a market-header child component
  so we can emit a close event if needed */
  headerNavigateEventHandler(event) {
    const { detail, target } = event;
    // TODO: 'close' should probably come from an enum of some sort
    if (detail.action === 'close') {
      // only dismiss if this is the first ancestor dialog
      if (target.closest(getDialogSelector()) === this.el) {
        this.dismiss();
      }
    }
  }
  /**
   * Emits the dismiss event
   * The parent context will handle actually removing elements from the DOM,
   * All the blade needs to do it emit an event so actually closing it can be
   * some other elements problem
   */
  dismiss(options) {
    const { defaultPrevented } = this.marketDialogDismissed.emit({
      dialog: this.el,
      type: this.type,
      origin: (options === null || options === void 0 ? void 0 : options.origin) || this.el,
    });
    if (!defaultPrevented) {
      this.hidden = true;
      /**
       * Emit a marketDialogDidDismiss event when modal gets fully dismissed (after animation).
       */
      setTimeout(() => {
        this.marketDialogDidDismiss.emit({
          dialog: this.el,
          type: this.type,
          origin: this.el,
        });
      }, this.animationExitDuration);
    }
    return Promise.resolve();
  }
  onTrapFocusChanged(newValue, oldValue) {
    if (newValue !== oldValue) {
      if (newValue) {
        this.activateFocusTrap();
      }
      else {
        this.deactivateFocusTrap();
      }
    }
  }
  /**
   * Activates the focus trap
   * @param {Object} [options] [focus-trap `.createFocusTrap` options](https://github.com/focus-trap/focus-trap#createoptions)
   * @param {Object} [activateOptions] [focus-trap `.activate` options](https://github.com/focus-trap/focus-trap#trapactivate)
   */
  activateFocusTrap(options, activateOptions) {
    if (this.focusTrap) {
      this.focusTrap.activate(activateOptions);
      if (!this.trapFocus) {
        this.trapFocus = true;
      }
    }
    else {
      this.focusTrap = createAndActivateFocusTrap(this.el, options, activateOptions);
    }
    this.focusTrapActivateOptions = activateOptions;
    return Promise.resolve();
  }
  /**
   * Deactivates the focus trap
   * @param {Object} [deactivateOptions] [focus-trap `.deactivate` options](https://github.com/focus-trap/focus-trap#trapdeactivate)
   */
  deactivateFocusTrap(deactivateOptions) {
    if (this.focusTrap) {
      this.focusTrap.deactivate(deactivateOptions);
      this.focusTrap = undefined;
    }
    return Promise.resolve();
  }
  initContentObserver() {
    if (!this.observers.content) {
      this.observers.content = new MutationObserver(async (mutations) => {
        if (this.trapFocus && this.focusTrap) {
          this.focusTrap.deactivate();
          await waitForAddedNodesToBeReady(mutations);
          this.focusTrap.activate(this.focusTrapActivateOptions);
        }
      });
      this.observers.content.observe(this.el, {
        childList: true,
        subtree: true,
      });
    }
  }
  connectedCallback() {
    /**
     * Emit a dialogLoaded event when the component connects. Need this so
     * the context manager isn't rummaging around it's DOM to try and find the
     * dialog that was just appended
     */
    setTimeout(() => {
      this.marketDialogLoaded.emit({
        dialog: this.el,
        type: this.type,
      });
      if (this.trapFocus) {
        this.activateFocusTrap();
      }
    }, this.animationEnterDuration);
  }
  componentWillLoad() {
    const header = this.el.querySelector(getNamespacedTagFor('market-header'));
    if (header) {
      // We want to force the header to be navigable when slotted into blade
      header.showNavigation = true;
    }
    if (this.hidden) {
      this.skipAnimation = true;
    }
    setupDialogCompactHandler(this.el);
  }
  componentDidLoad() {
    this.initContentObserver();
  }
  disconnectedCallback() {
    this.deactivateFocusTrap();
  }
  render() {
    return (h(Host, { role: "dialog", class: classNames('market-blade', {
        'skip-animation': this.skipAnimation,
      }) }, h("slot", null)));
  }
  get el() { return getElement(this); }
  static get watchers() { return {
    "hidden": ["reenableAnimation"],
    "trapFocus": ["onTrapFocusChanged"]
  }; }
};
MarketBlade.style = marketBladeCss;

export { MarketBlade as market_blade };
